import React, { useEffect } from 'react';
import {
  ArrowRight,
  Bot,
  Briefcase,
  Building2,
  Bus,
  ChevronRight,
  Code2,
  Cpu,
  Globe,
  Handshake,
  Heart,
  Lightbulb,
  MessageCircle,
  Network,
  Send,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  TrendingUp,
  UserPlus,
  Users,
  Wallet
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useLanguage } from '../context/LanguageContext';
import { getWsjCommercialWhatsappUrl } from '@/lib/wsjCommercialWhatsapp';
import { AmbassadorsHeroVisual } from '../components/ambassadors/AmbassadorsHeroVisual';
import { AmbassadorsSignupForm } from '../components/ambassadors/AmbassadorsSignupForm';

const howIcons = [UserPlus, Send, Handshake, Wallet];
const serviceIcons = [Globe, ShoppingBag, Bot, TrendingUp, Smartphone, Cpu, Code2, Bus];
const commissionIcons = [ShieldCheck, Wallet, Sparkles, MessageCircle];
const whoIcons = [Users, Heart, Briefcase, Building2, Lightbulb, Network];

const Ambassadors = () => {
  const { t, language } = useLanguage();
  const p = t.ambassadorsPage;

  useEffect(() => {
    const prev = document.title;
    document.title = p.metaTitle;
    return () => {
      document.title = prev;
    };
  }, [p.metaTitle]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openWhatsapp = () => {
    window.open(getWsjCommercialWhatsappUrl(p.whatsappMessage), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen w-full min-w-0 max-w-full overflow-x-hidden">
      <Header />
      <main className="relative overflow-x-hidden pt-24 sm:pt-28 pb-8">
        <div className="absolute inset-0 tech-grid opacity-[0.18] pointer-events-none" />
        <div className="absolute top-24 left-1/4 w-[min(420px,calc(100vw-2rem))] max-w-[100vw] h-[min(420px,70vw)] bg-emerald-500/[0.07] rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-40 right-0 w-[min(380px,calc(100vw-2rem))] max-w-[100vw] h-[min(380px,65vw)] bg-amber-500/[0.06] rounded-full blur-[90px] pointer-events-none" />
        <div className="absolute top-1/2 left-0 w-[min(300px,calc(100vw-2rem))] h-[min(300px,50vw)] bg-indigo-500/[0.04] rounded-full blur-[80px] pointer-events-none" />

        <div className="relative z-10 container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8">
          {/* HERO */}
          <section className="min-w-0 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 xl:gap-16 items-center mb-16 sm:mb-24 lg:mb-28">
            <div className="text-center lg:text-left pt-2 min-w-0">
              <div className="inline-flex max-w-full min-w-0 items-center justify-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/[0.06] px-4 py-1.5 text-xs font-semibold tracking-wide text-emerald-200/90 mb-5">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400 shrink-0" aria-hidden />
                <span className="break-words text-center">{p.badge}</span>
              </div>
              <h1 className="text-[1.65rem] sm:text-4xl lg:text-[2.65rem] xl:text-5xl font-black text-white leading-[1.12] tracking-tight mb-5 break-words">
                {p.heroTitle}
              </h1>
              <p className="text-base sm:text-lg text-white/55 font-light leading-relaxed max-w-xl mx-auto lg:mx-0 mb-5 break-words">
                {p.heroSubtitle}
              </p>
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] px-4 py-3 mb-6 max-w-xl mx-auto lg:mx-0">
                <p className="text-sm text-emerald-100/85 font-medium leading-relaxed">{p.heroHighlight}</p>
              </div>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-8">
                {p.heroTrustBadges.map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium text-white/65 backdrop-blur-sm"
                  >
                    {badge}
                  </span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center lg:justify-start gap-3">
                <Button
                  type="button"
                  size="lg"
                  className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-lg shadow-amber-900/25 h-12 px-6"
                  onClick={() => scrollTo('ambassadors-signup')}
                >
                  {p.btnJoin}
                  <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="rounded-xl border-white/20 bg-white/[0.04] text-white hover:bg-white/10 h-12 px-6"
                  onClick={() => scrollTo('ambassadors-how-it-works')}
                >
                  {p.btnHowItWorks}
                  <ChevronRight className="w-4 h-4 ml-1" aria-hidden />
                </Button>
              </div>
            </div>
            <div className="mt-10 lg:mt-0 relative min-w-0">
              <AmbassadorsHeroVisual copy={p} />
            </div>
          </section>

          {/* COMO FUNCIONA */}
          <section id="ambassadors-how-it-works" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-10 sm:mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{p.sectionHowTitle}</h2>
              <p className="text-white/50 mt-4 max-w-2xl mx-auto font-light leading-relaxed">{p.sectionHowLead}</p>
              <div className="w-20 h-1 bg-gradient-to-r from-emerald-500 to-amber-500 mx-auto rounded-full mt-6" />
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {p.howSteps.map((step, i) => {
                const Icon = howIcons[i] || Sparkles;
                return (
                  <article
                    key={step.title}
                    className="group relative flex flex-col rounded-[1.5rem] border border-white/[0.1] bg-slate-950/35 backdrop-blur-md p-6 transition-all duration-300 hover:border-white/20 hover:shadow-[0_20px_60px_-30px_rgba(255,255,255,0.08)]"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-emerald-500/10 border border-white/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-amber-300/90" aria-hidden />
                      </div>
                      <span className="text-xs font-bold text-white/30 tabular-nums">0{i + 1}</span>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-sm text-white/50 font-light leading-relaxed flex-1">{step.desc}</p>
                  </article>
                );
              })}
            </div>
          </section>

          {/* SERVIÇOS */}
          <section id="ambassadors-services" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-10 sm:mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{p.sectionServicesTitle}</h2>
              <p className="text-white/50 mt-4 max-w-2xl mx-auto font-light leading-relaxed">{p.sectionServicesLead}</p>
              <div className="w-20 h-1 bg-gradient-to-r from-sky-500 to-indigo-500 mx-auto rounded-full mt-6" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {p.services.map((service, i) => {
                const Icon = serviceIcons[i] || Sparkles;
                return (
                  <article
                    key={service.title}
                    className="rounded-[1.35rem] border border-white/[0.08] bg-white/[0.025] p-5 hover:border-white/15 hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center mb-3">
                      <Icon className="w-4 h-4 text-sky-400/85" aria-hidden />
                    </div>
                    <h3 className="text-sm font-bold text-white mb-1.5">{service.title}</h3>
                    <p className="text-xs text-white/45 font-light leading-relaxed mb-3">{service.desc}</p>
                    <p className="text-[11px] text-emerald-300/75 font-medium leading-snug">{service.commissionNote}</p>
                  </article>
                );
              })}
            </div>
          </section>

          {/* COMISSÕES */}
          <section id="ambassadors-commissions" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-10 sm:mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{p.sectionCommissionsTitle}</h2>
              <p className="text-white/50 mt-4 max-w-2xl mx-auto font-light leading-relaxed">{p.sectionCommissionsLead}</p>
              <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-emerald-500 mx-auto rounded-full mt-6" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {p.commissionItems.map((item, i) => {
                const Icon = commissionIcons[i] || ShieldCheck;
                return (
                  <article
                    key={item.title}
                    className="flex gap-4 rounded-[1.35rem] border border-white/[0.1] bg-slate-950/40 p-5 sm:p-6"
                  >
                    <div className="w-10 h-10 shrink-0 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-amber-300/90" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-white/50 font-light leading-relaxed">{item.desc}</p>
                    </div>
                  </article>
                );
              })}
            </div>
            <p className="mt-6 text-xs sm:text-sm text-white/40 text-center max-w-3xl mx-auto leading-relaxed font-light px-4">
              {p.commissionDisclaimer}
            </p>
          </section>

          {/* QUEM PODE INDICAR + CLIENTE IDEAL */}
          <section className="max-w-6xl mx-auto mb-20 sm:mb-28 min-w-0">
            <div className="grid gap-8 lg:grid-cols-2">
              <div id="ambassadors-who" className="scroll-mt-28 rounded-[1.75rem] border border-white/[0.1] bg-slate-950/35 backdrop-blur-md p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight mb-2">{p.sectionWhoTitle}</h2>
                <p className="text-sm text-white/50 font-light leading-relaxed mb-6">{p.sectionWhoLead}</p>
                <ul className="space-y-3">
                  {p.whoCanRefer.map((line, i) => {
                    const Icon = whoIcons[i] || Users;
                    return (
                      <li key={line} className="flex gap-3 text-sm text-white/70 min-w-0">
                        <Icon className="w-4 h-4 shrink-0 text-emerald-400/85 mt-0.5" aria-hidden />
                        <span className="font-light">{line}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div id="ambassadors-ideal" className="scroll-mt-28 rounded-[1.75rem] border border-indigo-400/25 bg-gradient-to-b from-indigo-500/[0.08] via-slate-950/50 to-slate-950/70 p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight mb-2">{p.sectionIdealTitle}</h2>
                <p className="text-sm text-white/55 font-light leading-relaxed mb-6">{p.sectionIdealLead}</p>
                <ul className="space-y-2.5">
                  {p.idealClients.map((line) => (
                    <li key={line} className="flex gap-2.5 text-sm text-white/65 min-w-0">
                      <ChevronRight className="w-4 h-4 shrink-0 text-indigo-400/80 mt-0.5" aria-hidden />
                      <span className="font-light leading-relaxed">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* FORMULÁRIO */}
          <section id="ambassadors-signup" className="max-w-3xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{p.sectionFormTitle}</h2>
              <p className="text-white/50 mt-4 font-light leading-relaxed">{p.sectionFormLead}</p>
              <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto rounded-full mt-6" />
            </div>
            <AmbassadorsSignupForm copy={p} language={language} whatsappMessage={p.whatsappMessage} />
          </section>

          {/* FAQ */}
          <section id="ambassadors-faq" className="max-w-3xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{p.sectionFaqTitle}</h2>
              <p className="text-white/50 mt-4 font-light leading-relaxed">{p.sectionFaqLead}</p>
              <div className="w-20 h-1 bg-gradient-to-r from-sky-500 to-indigo-500 mx-auto rounded-full mt-6" />
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {p.faqItems.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`faq-${i}`}
                  className="rounded-xl border border-white/[0.08] bg-slate-950/40 px-4 sm:px-5 overflow-hidden"
                >
                  <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-white/85 hover:text-white hover:no-underline py-4">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-white/50 font-light leading-relaxed pb-4">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* CTA FINAL */}
          <section className="max-w-4xl mx-auto mb-12 sm:mb-16 min-w-0">
            <div className="relative rounded-[1.75rem] border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.1] via-slate-950/80 to-emerald-500/[0.06] p-8 sm:p-12 text-center overflow-hidden">
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-emerald-500/10 opacity-80"
                aria-hidden
              />
              <div className="relative">
                <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-3 break-words">{p.finalCtaTitle}</h2>
                <p className="text-white/55 font-light leading-relaxed max-w-xl mx-auto mb-8">{p.finalCtaSubtitle}</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button
                    type="button"
                    size="lg"
                    className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-lg shadow-amber-900/25 h-12 px-8"
                    onClick={() => scrollTo('ambassadors-signup')}
                  >
                    {p.finalCtaButton}
                    <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    size="lg"
                    variant="outline"
                    className="rounded-xl border-emerald-500/35 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15 h-12 px-6"
                    onClick={openWhatsapp}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" aria-hidden />
                    WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Ambassadors;
